import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Switch,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { Chip, FileUpload, Radio } from "@outpave/ds";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import CompanyPaymentCard from "components/common/cards/CompanyPaymentCard";
import type {
  CardFace,
  CompanyPaymentCardProps,
} from "components/common/cards/CompanyPaymentCard";
import { LOGO_ACCEPT } from "components/common/branding/BrandLogosPanel";

export interface CardFaceOption {
  face: CardFace;
  /** A translation key. */
  label: string;
  /** The swatch beside the label: an image or the face's colour. */
  swatch: SxProps<Theme>;
}

/** The draft card the preview shows, in `CompanyPaymentCard`'s terms. */
export type CardDesignPreview = Pick<
  CompanyPaymentCardProps,
  | "face"
  | "art"
  | "fills"
  | "textMode"
  | "backgroundColor"
  | "artwork"
  | "backgroundImage"
>;

interface CardDesignEditorProps {
  title: string;
  help: string;
  preview: CardDesignPreview;

  faceOptions: CardFaceOption[];
  /** The face the choice shows; empty when an older rule matches none. */
  shownFace: CardFace | "";
  /** The shown face comes from the platform rather than this record. */
  faceInherited?: boolean;
  onFaceChange: (face: CardFace) => void;

  /** A colour face picks its own readable text, so there is no choice to offer. */
  inkIsAutomatic: boolean;
  isLightText: boolean;
  /** The text colour comes from the platform rather than this record. */
  textInherited?: boolean;
  onTextModeChange: (mode: "light" | "dark") => void;

  artworkTitle: string;
  artworkHelp: string;
  hasArtwork: boolean;
  uploading: boolean;
  /** The page validates and stores the file; the editor only guards against a double drop. */
  onUpload: (file: File) => Promise<unknown> | void;
  onRemove: () => Promise<unknown> | void;
}

const isFileDrag = (event: React.DragEvent) =>
  Array.from(event.dataTransfer?.types ?? []).includes("Files");

const SCRIM = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "inherit",
} as const;

/**
 * The Card tab for company and platform alike: the card, which takes a click or
 * a dropped SVG, its face and text colour, and the artwork's actions.
 */
const CardDesignEditor: React.FC<CardDesignEditorProps> = ({
  title,
  help,
  preview,
  faceOptions,
  shownFace,
  faceInherited = false,
  onFaceChange,
  inkIsAutomatic,
  isLightText,
  textInherited = false,
  onTextModeChange,
  artworkTitle,
  artworkHelp,
  hasArtwork,
  uploading,
  onUpload,
  onRemove,
}) => {
  const { t } = useTranslation();
  // Claimed synchronously: `uploading` is a prop, so two drops in one tick both read it as false.
  const claimed = useRef(false);
  const [dragging, setDragging] = useState(false);
  const [hovered, setHovered] = useState(false);
  // Enter and leave fire for every child the drag crosses, so only the outermost leave ends it.
  const dragDepth = useRef(0);

  const startUpload = (file?: File) => {
    if (!file || uploading || claimed.current) return;
    claimed.current = true;
    Promise.resolve(onUpload(file)).finally(() => {
      claimed.current = false;
    });
  };

  const prompt = hasArtwork
    ? t("ClickOrDropSvgToReplace")
    : t("ClickOrDropSvgToUpload");

  const platformChip = (
    <Chip size="sm" tone="neutral">
      {t("Platform")}
    </Chip>
  );

  // The same scrim the card details use for "Click to reveal", so the card reads as one control.
  const overlay =
    uploading || dragging || hovered ? (
      <Box
        sx={{
          ...SCRIM,
          backgroundColor: "var(--color-bg-overlay)",
          // The scrim is dark in both schemes, so its text is the card's ink, not the page's.
          color: "var(--color-card-foreground)",
          border:
            dragging && !uploading
              ? "2px dashed var(--color-border-focus)"
              : undefined,
          pointerEvents: "none",
        }}
      >
        {uploading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          // The theme gives subtitle2 its own color, which the `color` prop does not override.
          <Typography variant="subtitle2" sx={{ color: "inherit" }}>
            {dragging ? t("DropSvgToUpload") : prompt}
          </Typography>
        )}
      </Box>
    ) : undefined;

  return (
    <FileUpload
      variant="trigger"
      accept={LOGO_ACCEPT}
      disabled={uploading}
      onFiles={(files) => startUpload(files[0])}
    >
      {(openPicker) => (
        <Stack gap="var(--space-5)">
          <Stack gap="var(--space-1)">
            <Typography variant="h6">{title}</Typography>
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-secondary)" }}
            >
              {help}
            </Typography>
          </Stack>

          {/* The card and its colours beside the artwork, stacking on a narrow screen. */}
          <Columns
            count={{ xs: 1, md: "minmax(0, 380px) 1fr" }}
            gap="var(--space-7)"
            sx={{ alignItems: "start" }}
          >
            <Stack gap="var(--space-4)" sx={{ width: "100%", maxWidth: 380 }}>
              {/* The card takes a click or a dropped file; the Upload button does the same. */}
              <Box
                data-testid="card-artwork-drop"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onDragEnter={(event) => {
                  if (!isFileDrag(event)) return;
                  event.preventDefault();
                  dragDepth.current += 1;
                  setDragging(true);
                }}
                onDragOver={(event) => {
                  if (isFileDrag(event)) event.preventDefault();
                }}
                onDragLeave={(event) => {
                  if (!isFileDrag(event)) return;
                  dragDepth.current = Math.max(0, dragDepth.current - 1);
                  if (!dragDepth.current) setDragging(false);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  dragDepth.current = 0;
                  setDragging(false);
                  startUpload(event.dataTransfer?.files?.[0]);
                }}
              >
                <CompanyPaymentCard
                  width="100%"
                  {...preview}
                  holder={t("First Name Last Name")}
                  number="•••• •••• •••• 1234"
                  expiry="12/25"
                  onClick={uploading ? undefined : openPicker}
                  actionLabel={prompt}
                  overlay={overlay}
                />
              </Box>

              <RadioGroup
                value={shownFace}
                aria-label={title}
                onChange={(event) =>
                  onFaceChange(event.target.value as CardFace)
                }
                sx={{ gap: "var(--space-1)" }}
              >
                {faceOptions.map((option) => (
                  <FormControlLabel
                    key={option.face}
                    value={option.face}
                    control={<Radio />}
                    // Locked while artwork uploads: a page may change the face when it lands.
                    disabled={uploading}
                    sx={{
                      margin: 0,
                      paddingInline: "var(--space-2)",
                      borderRadius: "var(--radius-md)",
                      backgroundColor:
                        shownFace === option.face
                          ? "var(--color-brand-8)"
                          : undefined,
                    }}
                    label={
                      <Inline gap="var(--space-3)" wrap={false}>
                        <Box
                          aria-hidden
                          sx={[
                            {
                              width: 16,
                              height: 16,
                              flexShrink: 0,
                              borderRadius: "var(--radius-pill)",
                              border:
                                "var(--border-width-1) solid var(--color-border)",
                            },
                            ...(Array.isArray(option.swatch)
                              ? option.swatch
                              : [option.swatch]),
                          ]}
                        />
                        {t(option.label)}
                        {faceInherited &&
                          shownFace === option.face &&
                          platformChip}
                      </Inline>
                    }
                  />
                ))}
              </RadioGroup>

              {inkIsAutomatic ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--color-text-tertiary)",
                    textAlign: "center",
                  }}
                >
                  {t("CardInkAutomatic")}
                </Typography>
              ) : (
                <FormControlLabel
                  sx={{ margin: 0 }}
                  control={
                    <Switch
                      checked={isLightText}
                      onChange={(event) =>
                        onTextModeChange(
                          event.target.checked ? "light" : "dark",
                        )
                      }
                    />
                  }
                  label={
                    <Inline gap="var(--space-3)" wrap={false}>
                      {t(isLightText ? "Light Text" : "Dark Text")}
                      {textInherited && platformChip}
                    </Inline>
                  }
                />
              )}
            </Stack>

            <Stack gap="var(--space-3)" sx={{ minWidth: 0 }}>
              <Stack gap="var(--space-1)">
                <Typography variant="h6">{artworkTitle}</Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "var(--color-text-secondary)" }}
                >
                  {artworkHelp}
                </Typography>
              </Stack>
              <Inline gap="var(--space-2)">
                <Button
                  size="small"
                  variant={hasArtwork ? "default-outlined" : "primary-outlined"}
                  disabled={uploading}
                  onClick={openPicker}
                  aria-label={
                    hasArtwork
                      ? t("ReplaceLogo", { label: artworkTitle })
                      : undefined
                  }
                >
                  {hasArtwork ? t("Replace") : t("UploadSvg")}
                </Button>
                {hasArtwork && (
                  <Button
                    size="small"
                    variant="danger-outlined"
                    disabled={uploading}
                    onClick={() => onRemove()}
                    aria-label={t("RemoveLogo", { label: artworkTitle })}
                  >
                    {t("Remove")}
                  </Button>
                )}
              </Inline>
              <Typography
                variant="caption"
                sx={{ color: "var(--color-text-secondary)" }}
              >
                {t("CardArtworkHint")}
              </Typography>
            </Stack>
          </Columns>
        </Stack>
      )}
    </FileUpload>
  );
};

export default CardDesignEditor;
